import React from 'react'
import KMK_LOADING from '@public/assets/common/kmk_loading.gif'
import type {
  NarrativeOcgValidationAction,
  NarrativeOcgValidationHint,
} from '../types'
import { getPanelClassName } from '../utils/classNames'
import { getNarrativeSeverity, hasInstructions } from '../utils/issueDetails'
import { NarrativeDiffText } from './NarrativeDiffText'
import { NarrativeOcgReferenceActions } from './NarrativeOcgReferenceActions'
import { NarrativeOcgValidationBadges } from './NarrativeOcgValidationBadges'
import NarrativeOcgActions from './actions/NarrativeOcgActions'

type NarrativeOcgValidationPanelProps = {
  validationHint?: NarrativeOcgValidationHint
  onAcceptValidation?: (suggestedNarrative: string) => void
  onDiscardValidation?: () => void
  onRevertValidation?: () => void
  onRewriteValidation?: () => void
  actionLoading?: NarrativeOcgValidationAction | null
  suggestionLoading?: boolean
  onFloatingWindowInteraction?: (isOpen?: boolean) => void
  isMobile?: boolean
  dialogActions?: boolean
}

export const NarrativeOcgValidationPanel = ({
  validationHint,
  onAcceptValidation,
  onDiscardValidation,
  onRevertValidation,
  onRewriteValidation,
  actionLoading,
  suggestionLoading = false,
  onFloatingWindowInteraction,
  isMobile = false,
  dialogActions = false,
}: NarrativeOcgValidationPanelProps) => {
  const severity = getNarrativeSeverity(validationHint)
  const instructionsPresent = hasInstructions(validationHint)
  const suggestedNarrative = validationHint?.suggestedNarrative
  const hasSuggestedNarrative = typeof suggestedNarrative === 'string'
  const panelSeverity =
    severity ??
    (validationHint?.source === 'ai' && hasSuggestedNarrative ? 'ai' : undefined)
  const acceptedNarrative = hasSuggestedNarrative
    ? suggestedNarrative
    : validationHint?.originalNarrative
  const canAcceptValidation =
    typeof acceptedNarrative === 'string' && acceptedNarrative.trim().length > 0
  const canDiscard = severity === 'warning'
  const isSuggestionLoading =
    Boolean(severity) &&
    (suggestionLoading || actionLoading === 'rewrite')
  const isActionBusy = Boolean(actionLoading) || isSuggestionLoading
  const handlePanelInteraction = (event: React.SyntheticEvent) => {
    onFloatingWindowInteraction?.()
    if (event.type !== 'click') event.preventDefault()
    event.stopPropagation()
  }
  const handleActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    action?: () => void
  ) => {
    onFloatingWindowInteraction?.()
    event.preventDefault()
    event.stopPropagation()
    if (isActionBusy) return
    action?.()
  }
  if (validationHint?.isLoading) return null
  if (!validationHint?.visible) return null
  if (!panelSeverity) return null

  return (
    <div
      className={getPanelClassName(panelSeverity)}
      onClick={handlePanelInteraction}
      onMouseDown={handlePanelInteraction}
      onTouchStart={handlePanelInteraction}
    >
      <div className="narrative-ocg-panel__header mb-1.5 flex items-center justify-between gap-3">
        <div className="narrative-ocg-panel__title text-xs font-bold leading-[1.33] tracking-[0.5px]">
          MIRA Suggestion
        </div>
        {!dialogActions ? (
          <NarrativeOcgValidationBadges
            validationHint={validationHint}
            onFloatingWindowInteraction={onFloatingWindowInteraction}
            isMobile={isMobile}
          />
        ) : null}
      </div>
      {isSuggestionLoading ? (
        <div
          aria-label="Generating narrative suggestion"
          className="narrative-ocg-panel__body flex min-h-8 items-center justify-center py-3"
          role="status"
        >
          {/* OCG work uses the KMK validating animation. */}
          <img
            alt=""
            src={KMK_LOADING}
            width={48}
            height={48}
            className="block object-contain"
          />
        </div>
      ) : instructionsPresent ? (
        <div className="narrative-ocg-panel__body min-h-8 whitespace-pre-wrap text-xs leading-4 tracking-[0.5px] text-[#333]">
          {(validationHint.instructions ?? []).join('\n')}
        </div>
      ) : hasSuggestedNarrative ? (
        <div className="narrative-ocg-panel__body min-h-8 whitespace-pre-wrap text-xs leading-4 tracking-[0.5px] text-[#333]">
          <NarrativeDiffText
            original={validationHint.originalNarrative}
            revised={suggestedNarrative}
          />
        </div>
      ) : null}
      <div
        className={`narrative-ocg-panel__footer mt-2.5 flex gap-4 ${dialogActions ? '!justify-end' : 'justify-between'} ${isMobile ? 'narrative-ocg-panel__footer--mobile items-end' : 'items-center max-[640px]:flex-col max-[640px]:items-start'}`}
      >
        {!dialogActions ? (
          <NarrativeOcgReferenceActions
            validationHint={validationHint}
            onFloatingWindowInteraction={onFloatingWindowInteraction}
            isMobile={isMobile}
          />
        ) : null}
        <NarrativeOcgActions
          isMobile={isMobile}
          suggestedNarrative={acceptedNarrative}
          canAcceptValidation={canAcceptValidation}
          canDiscard={canDiscard}
          isActionBusy={isActionBusy}
          actionLoading={actionLoading}
          onAcceptValidation={onAcceptValidation}
          onDiscardValidation={onDiscardValidation}
          onRevertValidation={onRevertValidation}
          onRewriteValidation={onRewriteValidation}
          handleActionClick={handleActionClick}
          showDecisionActions
          showSuggestionActions={!instructionsPresent}
          showAcceptAction={!instructionsPresent}
        />
      </div>
    </div>
  )
}
